import React, { useMemo, useState } from 'react';
import { Card, Col, Flex, InputNumber, Row, Slider, Typography, Segmented } from 'antd';
import './ClubCalculator.css';
import { COLOURS } from '../../colours';
import {
  calculateClubSavings,
  CLUB_CALCULATOR_DEFAULTS,
} from './calculatorModel';

const { Title, Paragraph, Text } = Typography;

const formatMoney = (value) => {
  const amount = Number(value || 0);
  return amount < 0 ? `-£${Math.abs(amount).toFixed(2)}` : `£${amount.toFixed(2)}`;
};
const formatHours = (value) => `${Number(value || 0).toFixed(1)} hrs`;

const ClubCalculator = () => {
  const [members, setMembers] = useState(CLUB_CALCULATOR_DEFAULTS.members);
  const [sessionsPerWeek, setSessionsPerWeek] = useState(CLUB_CALCULATOR_DEFAULTS.sessionsPerWeek);
  const [playersPerSession, setPlayersPerSession] = useState(CLUB_CALCULATOR_DEFAULTS.playersPerSession);
  const [sessionFee, setSessionFee] = useState(CLUB_CALCULATOR_DEFAULTS.sessionFee);
  const [adminMinutesPerSession, setAdminMinutesPerSession] = useState(CLUB_CALCULATOR_DEFAULTS.adminMinutesPerSession);
  const [hourlyAdminValue, setHourlyAdminValue] = useState(CLUB_CALCULATOR_DEFAULTS.hourlyAdminValue);
  const [currentPaymentMethod, setCurrentPaymentMethod] = useState(CLUB_CALCULATOR_DEFAULTS.currentPaymentMethod);
  const [currentProcessorFeePerPayment, setCurrentProcessorFeePerPayment] = useState(CLUB_CALCULATOR_DEFAULTS.currentProcessorFeePerPayment);
  const [shuttleOpsFeePayer, setShuttleOpsFeePayer] = useState(CLUB_CALCULATOR_DEFAULTS.shuttleOpsFeePayer);
  const [bankTransferReconciliationMinutes, setBankTransferReconciliationMinutes] = useState(CLUB_CALCULATOR_DEFAULTS.bankTransferReconciliationMinutes);
  const [avgSessionsPerPlayerPerMonth, setAvgSessionsPerPlayerPerMonth] = useState(CLUB_CALCULATOR_DEFAULTS.avgSessionsPerPlayerPerMonth);
  const [recoveredPaymentsPerMonth, setRecoveredPaymentsPerMonth] = useState(CLUB_CALCULATOR_DEFAULTS.recoveredPaymentsPerMonth);
  const [recoveredPaymentValue, setRecoveredPaymentValue] = useState(CLUB_CALCULATOR_DEFAULTS.recoveredPaymentValue);
  const [wantsWhatsApp, setWantsWhatsApp] = useState(CLUB_CALCULATOR_DEFAULTS.wantsWhatsApp);

  const results = useMemo(() => {
    return calculateClubSavings({
      members,
      sessionsPerWeek,
      playersPerSession,
      sessionFee,
      adminMinutesPerSession,
      hourlyAdminValue,
      currentPaymentMethod,
      currentProcessorFeePerPayment,
      shuttleOpsFeePayer,
      bankTransferReconciliationMinutes,
      avgSessionsPerPlayerPerMonth,
      recoveredPaymentsPerMonth,
      recoveredPaymentValue,
      wantsWhatsApp,
    });
  }, [
    members,
    sessionsPerWeek,
    playersPerSession,
    sessionFee,
    adminMinutesPerSession,
    hourlyAdminValue,
    currentPaymentMethod,
    currentProcessorFeePerPayment,
    shuttleOpsFeePayer,
    bankTransferReconciliationMinutes,
    avgSessionsPerPlayerPerMonth,
    recoveredPaymentsPerMonth,
    recoveredPaymentValue,
    wantsWhatsApp,
  ]);

  return (
    <div className="club-calculator-section">
      <div className="club-calculator-inner">
        <Flex vertical gap={16} style={{ marginBottom: 32 }}>
          <Title level={2} style={{ marginBottom: 0 }}>
            Club savings calculator
          </Title>
          <Paragraph style={{ maxWidth: 900, marginBottom: 0 }}>
            Estimate the monthly value of saved admin time and recovered payments.
            Player-paid processing fees stay separate from your club's costs.
          </Paragraph>
        </Flex>

        <Row gutter={[24, 24]}>
          <Col xs={24} lg={14}>
            <Card className="club-calculator-card">
              <Flex vertical gap={28}>
                <Title level={3} style={{ margin: 0 }}>Your club</Title>
                <div>
                  <Text strong>Club members: {members}</Text>
                  <Slider min={5} max={200} value={members} onChange={setMembers} />
                </div>

                <div>
                  <Text strong>Sessions per week: {sessionsPerWeek}</Text>
                  <Slider min={1} max={5} value={sessionsPerWeek} onChange={setSessionsPerWeek} />
                </div>

                <div>
                  <Text strong>Average players per session: {playersPerSession}</Text>
                  <Slider min={4} max={30} value={playersPerSession} onChange={setPlayersPerSession} />
                </div>

                <div>
                  <Text strong>Average session fee: £{sessionFee}</Text>
                  <Slider min={3} max={15} value={sessionFee} onChange={setSessionFee} />
                </div>

                <div>
                  <Text strong>
                    Average time spent on admin per session: {adminMinutesPerSession} mins
                  </Text>
                  <Slider
                    min={1}
                    max={30}
                    step={1}
                    value={adminMinutesPerSession}
                    onChange={setAdminMinutesPerSession}
                  />
                </div>

                <div>
                  <Text strong>Value of organiser time: £{hourlyAdminValue}/hr</Text>
                  <Slider
                    min={0}
                    max={30}
                    value={hourlyAdminValue}
                    onChange={setHourlyAdminValue}
                  />
                </div>

                <div>
                  <Text strong>How do members mostly pay today?</Text>
                  <div style={{ marginTop: 12 }}>
                    <Segmented
                      block
                      value={currentPaymentMethod}
                      onChange={setCurrentPaymentMethod}
                      options={[
                        { label: 'Bank transfer', value: 'bank_transfer' },
                        { label: 'Card per session', value: 'card_per_session' },
                        { label: 'Mixed', value: 'mixed' },
                        { label: 'Monthly club payments', value: 'monthly_membership' },
                      ]}
                    />
                  </div>
                </div>

                <div>
                  <Text strong>Who pays ShuttleOps processing fees?</Text>
                  <Paragraph className="calculator-field-help">
                    Choose the real payer. Player-paid fees never count as club costs.
                  </Paragraph>
                  <Segmented
                    block
                    value={shuttleOpsFeePayer}
                    onChange={setShuttleOpsFeePayer}
                    options={[
                      { label: 'Players', value: 'players' },
                      { label: 'Club', value: 'club' },
                    ]}
                  />
                </div>

                <div>
                  <Text strong>Estimated missed payments recovered each month</Text>
                  <Paragraph className="calculator-field-help">
                    Use your club's estimate. ShuttleOps cannot guarantee recovery.
                  </Paragraph>
                  <div className="calculator-recovery-inputs">
                    <label>
                      <span>Payments recovered</span>
                      <InputNumber
                        aria-label="Estimated missed payments recovered each month"
                        min={0}
                        max={50}
                        step={1}
                        precision={0}
                        value={recoveredPaymentsPerMonth}
                        onChange={(value) => setRecoveredPaymentsPerMonth(value ?? 0)}
                      />
                    </label>
                    <label>
                      <span>Value of each payment</span>
                      <InputNumber
                        aria-label="Estimated value of each recovered payment"
                        min={0}
                        max={500}
                        step={1}
                        precision={2}
                        value={recoveredPaymentValue}
                        onChange={(value) => setRecoveredPaymentValue(value ?? 0)}
                        prefix="£"
                      />
                    </label>
                  </div>
                </div>

                <div>
                  <Text strong>Need WhatsApp reminders and assistant features?</Text>
                  <div style={{ marginTop: 12 }}>
                    <Segmented
                      block
                      value={wantsWhatsApp ? 'yes' : 'no'}
                      onChange={(value) => setWantsWhatsApp(value === 'yes')}
                      options={[
                        { label: 'No', value: 'no' },
                        { label: 'Yes', value: 'yes' },
                      ]}
                    />
                  </div>
                </div>

                <details className="calculator-assumptions calculator-advanced">
                  <summary>Advanced payment details</summary>
                  <Flex vertical gap={24}>
                    <div>
                      <Text strong>
                        Average sessions per player each month: {avgSessionsPerPlayerPerMonth}
                      </Text>
                      <Slider
                        min={1}
                        max={12}
                        step={1}
                        value={avgSessionsPerPlayerPerMonth}
                        onChange={setAvgSessionsPerPlayerPerMonth}
                      />
                    </div>

                    {(currentPaymentMethod === 'bank_transfer' || currentPaymentMethod === 'mixed') && (
                      <div>
                        <Text strong>
                          Minutes to check one bank transfer: {bankTransferReconciliationMinutes.toFixed(1)} mins
                        </Text>
                        <Slider
                          min={0.5}
                          max={5}
                          step={0.1}
                          value={bankTransferReconciliationMinutes}
                          onChange={setBankTransferReconciliationMinutes}
                        />
                      </div>
                    )}

                    {currentPaymentMethod !== 'bank_transfer' && (
                      <div>
                        <Text strong>Club cost per payment today</Text>
                        <Paragraph className="calculator-field-help">
                          Enter the amount your club pays. We won't assume another provider's rate.
                        </Paragraph>
                        <InputNumber
                          aria-label="Club cost per payment today"
                          min={0}
                          max={10}
                          step={0.01}
                          precision={2}
                          value={currentProcessorFeePerPayment}
                          onChange={setCurrentProcessorFeePerPayment}
                          placeholder="Enter a fee"
                          prefix="£"
                        />
                      </div>
                    )}

                    <Paragraph className="calculator-assumption-summary">
                      A month has 4.33 weeks. Time savings use fixed estimates by plan.
                      Payment fees use both separate 1% fees and 20p minimums.
                    </Paragraph>
                  </Flex>
                </details>
              </Flex>
            </Card>
          </Col>

          <Col xs={24} lg={10}>
            <Flex vertical gap={16}>
              <Card className="club-calculator-card result-card">
                <Flex vertical gap={8}>
                  <Text className="result-label">Your club likely handles</Text>
                  <Paragraph style={{ marginBottom: 0 }}>
                    {results.monthlySessions.toFixed(1)} sessions per month
                  </Paragraph>
                  <Paragraph style={{ marginBottom: 0 }}>
                    {results.monthlyAttendances.toFixed(0)} attendance charges
                  </Paragraph>
                  <Paragraph style={{ marginBottom: 0 }}>
                    {Math.round(results.currentPaymentEvents)} current payment events
                  </Paragraph>
                </Flex>
              </Card>

              {results.isAbovePublicLimit ? (
                <Card className="club-calculator-card result-card result-card-primary">
                  <Flex vertical gap={12}>
                    <Text className="result-label">Member capacity</Text>
                    <Title level={2} style={{ margin: 0, color: COLOURS.WHITE }}>
                      {results.inputs.members} members
                    </Title>
                    <Paragraph style={{ margin: 0, color: COLOURS.WHITE }}>
                      {results.explanation}
                    </Paragraph>
                    <a
                      className="calculator-demo-link"
                      href="https://meet.brevo.com/andy-feltham/intro-to-shuttleops"
                    >
                      Request a demo
                    </a>
                  </Flex>
                </Card>
              ) : (
                <>
                  <Card className="club-calculator-card result-card result-card-primary">
                    <Flex vertical gap={8}>
                      <Text className="result-label">Estimated net monthly value</Text>
                      <Title level={2} style={{ margin: 0, color: COLOURS.WHITE }}>
                        {Number.isFinite(results.netMonthlyBenefit)
                          ? formatMoney(results.netMonthlyBenefit)
                          : 'Add your current cost'}
                      </Title>
                      <Paragraph style={{ margin: 0, color: COLOURS.WHITE }}>
                        Saved time and recoverable payments, minus club-paid costs.
                      </Paragraph>
                    </Flex>
                  </Card>

                  <Card className="club-calculator-card result-card calculator-value-card">
                    <div className="calculator-value-row">
                      <span>Saved admin time</span>
                      <strong>{formatMoney(results.adminValueSaved)}</strong>
                      <small>{formatHours(results.adminHoursSaved)} each month</small>
                    </div>
                    <div className="calculator-value-row">
                      <span>Estimated recovered payments</span>
                      <strong>{formatMoney(results.recoveredPaymentValueTotal)}</strong>
                      <small>{recoveredPaymentsPerMonth} at {formatMoney(recoveredPaymentValue)} each</small>
                    </div>
                    <div className="calculator-value-row calculator-cost-row">
                      <span>{results.recommendedPlan} plan</span>
                      <strong>
                        {results.planPrice === 0 ? formatMoney(0) : `-${formatMoney(results.planPrice)}`}
                      </strong>
                      <small>{results.planPrice === 0 ? 'Free plan' : 'Club cost each month'}</small>
                    </div>
                    <div className="calculator-value-row calculator-cost-row">
                      <span>Club-paid processing fees</span>
                      <strong>
                        {results.shuttleOpsClubPaymentFees === 0
                          ? formatMoney(0)
                          : `-${formatMoney(results.shuttleOpsClubPaymentFees)}`}
                      </strong>
                      <small>
                        {shuttleOpsFeePayer === 'players'
                          ? 'Players pay their own processing fees'
                          : 'Based on the payments above'}
                      </small>
                    </div>
                  </Card>

                  <Card className="club-calculator-card result-card calculator-break-even-card">
                    <Flex vertical gap={8}>
                      <Text className="result-label">Your break-even point</Text>
                      {Number.isFinite(results.breakEvenAdminMinutes) &&
                      Number.isFinite(results.breakEvenRecoveredPaymentCount) ? (
                        <Paragraph style={{ marginBottom: 0 }}>
                          Your club cost is covered by {results.breakEvenAdminMinutes.toFixed(0)} saved minutes
                          {' '}at {formatMoney(hourlyAdminValue)}/hour, or{' '}
                          {results.breakEvenRecoveredPaymentCount.toFixed(1)} recovered payments worth{' '}
                          {formatMoney(recoveredPaymentValue)} each.
                        </Paragraph>
                      ) : Number.isFinite(results.breakEvenAdminMinutes) ? (
                        <Paragraph style={{ marginBottom: 0 }}>
                          Your club cost is covered by {results.breakEvenAdminMinutes.toFixed(0)} saved minutes
                          {' '}at {formatMoney(hourlyAdminValue)}/hour.
                        </Paragraph>
                      ) : Number.isFinite(results.breakEvenRecoveredPaymentCount) ? (
                        <Paragraph style={{ marginBottom: 0 }}>
                          Your club cost is covered by {results.breakEvenRecoveredPaymentCount.toFixed(1)}
                          {' '}recovered payments worth {formatMoney(recoveredPaymentValue)} each.
                        </Paragraph>
                      ) : (
                        <Paragraph style={{ marginBottom: 0 }}>
                          Add a time value or recovered payment value to calculate break-even.
                        </Paragraph>
                      )}
                    </Flex>
                  </Card>

                  <Card className="club-calculator-card result-card">
                    <Flex vertical gap={8}>
                      <Text className="result-label">Why this plan</Text>
                      <Paragraph style={{ marginBottom: 0 }}>{results.explanation}</Paragraph>
                      {results.shuttleOpsPlayerPaymentFees > 0 && (
                        <Paragraph className="calculator-fee-note">
                          Players would pay about {formatMoney(results.shuttleOpsPlayerPaymentFees)}
                          {' '}in total processing fees. This amount is not a club cost.
                        </Paragraph>
                      )}
                    </Flex>
                  </Card>
                </>
              )}
            </Flex>
          </Col>
        </Row>

        <Paragraph style={{ maxWidth: '65ch', marginTop: 24 }}>
          The ongoing Free plan includes 10 members. Standard costs £4 a month and includes 25 members.
          {' '}Existing bolt-ons can add features or raise capacity, including 150 members. These are plan allowances, not a product limit.
        </Paragraph>

        <Card className="club-calculator-card calculator-proof-card">
          <Title level={3}>What 99 attendance charges means</Title>
          <Paragraph>
            Five real summer sessions created 19, 19, 27, 24, and 10 attendance charges. That totals 99 charges.
          </Paragraph>
          <Paragraph>
            ShuttleOps tracked each charge's success or failure and each live balance. The club received one payout.
          </Paragraph>
          <Paragraph>
            Attendance charges, player payment batches, and club payouts are separate measures.
          </Paragraph>
        </Card>
      </div>
    </div>
  );
};

export default ClubCalculator;
